import { ORDER_STATUSES, money, statusBadge } from '../utils/order';
import React, { useEffect, useState } from 'react';

import { Link } from 'react-router-dom';
import OrderService from '../services/order.service';
import Pagination from './Pagination';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import Spinner from './Spinner';

const OrderManagement = () => {
  const { t } = useTranslation();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [applied, setApplied] = useState({ status: '', q: '' });
  const PAGE_SIZE = 10;

  const resolveMsg = (err) => (err.response && err.response.data && err.response.data.message) || err.message;

  const load = (p = 1, filters = applied) => {
    setLoading(true);
    const params = { page: p, size: PAGE_SIZE };
    if (filters.status) params.status = filters.status;
    if (filters.q) params.q = filters.q;
    OrderService.getAll(params)
      .then((res) => {
        setOrders(res.data.items);
        setTotalPages(res.data.totalPages || 1);
        setTotalItems(res.data.totalItems || 0);
        setPage(res.data.currentPage || p);
      })
      .catch((err) => toast.error(resolveMsg(err)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyFilters = (e) => {
    if (e) e.preventDefault();
    const next = { status: statusFilter, q: search.trim() };
    setApplied(next);
    load(1, next);
  };

  const clearFilters = () => {
    setStatusFilter('');
    setSearch('');
    const next = { status: '', q: '' };
    setApplied(next);
    load(1, next);
  };

  const goToPage = (p) => {
    if (p < 1 || p > totalPages || p === page) return;
    load(p);
  };

  const changeStatus = (order, status) => {
    if (status === order.status) return;
    OrderService.updateStatus(order.id, status)
      .then((res) => {
        setOrders((list) => list.map((o) => (o.id === order.id ? res.data : o)));
        toast.success(t('orderManagement.statusChanged', { id: order.id, status: t(`orderStatus.${status}`, status) }));
      })
      .catch((err) => toast.error(resolveMsg(err)));
  };

  return (
    <div className='container mt-4'>
      <h3 className='mb-3'>
        {t('orderManagement.title')} {totalItems > 0 && <small className='text-muted'>{t('orderManagement.count', { count: totalItems })}</small>}
      </h3>

      <form className='row g-2 mb-3' onSubmit={applyFilters}>
        <div className='col-12 col-sm-3'>
          <select className='form-select' value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value=''>{t('orderManagement.allStatuses')}</option>
            {ORDER_STATUSES.map((s) => (
              <option key={s} value={s}>
                {t(`orderStatus.${s}`, s)}
              </option>
            ))}
          </select>
        </div>
        <div className='col-12 col-sm-5'>
          <input type='text' className='form-control' placeholder={t('orderManagement.searchPlaceholder')} value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div className='col-12 col-sm-4 d-flex gap-2'>
          <button type='submit' className='btn btn-outline-primary flex-grow-1'>
            {t('orderManagement.filter')}
          </button>
          {(applied.status || applied.q) && (
            <button type='button' className='btn btn-outline-secondary' onClick={clearFilters}>
              {t('orderManagement.clear')}
            </button>
          )}
        </div>
      </form>

      {loading ? (
        <Spinner />
      ) : orders.length === 0 ? (
        <p className='text-muted'>{t('orderManagement.noOrders')}</p>
      ) : (
        <div className='table-responsive rounded'>
          <table className='table table-hover align-middle'>
            <thead>
              <tr>
                <th style={{ width: 60 }}>#</th>
                <th>{t('orderManagement.date')}</th>
                <th>{t('orderManagement.customer')}</th>
                <th>{t('orderManagement.phone')}</th>
                <th style={{ width: 110 }}>{t('orderManagement.total')}</th>
                <th style={{ width: 160 }}>{t('orderManagement.status')}</th>
                <th style={{ width: 80 }}></th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => {
                const meta = statusBadge(o.status);
                return (
                  <tr key={o.id}>
                    <td>{o.id}</td>
                    <td>{new Date(o.createdAt).toLocaleDateString()}</td>
                    <td>{o.customer_name}</td>
                    <td>{o.phone}</td>
                    <td className='fw-bold'>{money(o.total_amount)}</td>
                    <td>
                      <span className={`badge ${meta.cls} me-2`}>{t(`orderStatus.${o.status}`, meta.label)}</span>
                      <select className='form-select form-select-sm mt-1' value={o.status} onChange={(e) => changeStatus(o, e.target.value)}>
                        {ORDER_STATUSES.map((s) => (
                          <option key={s} value={s}>
                            {t(`orderStatus.${s}`, s)}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <Link to={`/orders/${o.id}`} className='btn btn-sm btn-outline-primary'>
                        {t('orderManagement.view')}
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!loading && <Pagination page={page} totalPages={totalPages} onChange={goToPage} />}
    </div>
  );
};

export default OrderManagement;
